import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, Send, Instagram, Wrench } from "lucide-react";
import { toast } from "sonner";
import { apiClient, PHONE_NUMBER, PHONE_TEL, SALES_PHONE, SALES_TEL, SERVICE_PHONE, SERVICE_TEL, EMAIL, ADDRESS, INSTAGRAM } from "../lib/api";
import Seo from "../components/Seo";

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Lütfen ad, telefon ve mesaj alanlarını eksiksiz doldurun.");
      return;
    }
    setSending(true);
    try {
      await apiClient.post("/messages", form);
      toast.success("Mesajınız tarafımıza iletilmiştir. En kısa sürede sizinle iletişime geçeceğiz.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  const whatsappLink = `https://wa.me/${PHONE_TEL.replace(/[^0-9]/g, "")}`;

  return (
    <div data-testid="contact-page" className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
      <Seo
        title="İletişim"
        description="Gensan Motors ile iletişime geçin. Satış: 0537 412 12 24, Servis: 0554 583 12 63. İstanbul Maltepe showroom adresimizi ziyaret edin veya WhatsApp'tan yazın."
        keywords="Gensan Motors iletişim, motosiklet satış telefon, motor servis İstanbul"
        path="/iletisim"
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="block h-px w-12 bg-orange-500" />
            <span className="text-orange-500 font-bold uppercase tracking-[0.3em] text-xs">
              İletişim
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none tracking-tight max-w-4xl">
            Bizimle <span className="text-orange-500">İletişime</span> Geçin
          </h1>
          <p className="mt-6 text-zinc-400 text-lg max-w-2xl">
            Showroom'umuzu ziyaret edebilir, telefonla ya da WhatsApp üzerinden bize ulaşabilirsiniz. Size en uygun motoru birlikte bulalım.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact Info */}
          <div className="space-y-4">
            <a
              href={`tel:${SALES_TEL}`}
              data-testid="contact-sales-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <Phone size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Satış</div>
                <div className="font-display text-2xl font-bold group-hover:text-orange-500 transition-colors">
                  {SALES_PHONE}
                </div>
                <div className="text-sm text-zinc-400 mt-1">Sıfır ve ikinci el motor satışı</div>
              </div>
            </a>

            <a
              href={`tel:${SERVICE_TEL}`}
              data-testid="contact-service-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <Wrench size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Servis</div>
                <div className="font-display text-2xl font-bold group-hover:text-orange-500 transition-colors">
                  {SERVICE_PHONE}
                </div>
                <div className="text-sm text-zinc-400 mt-1">Bakım, onarım ve servis randevusu</div>
              </div>
            </a>

            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="contact-landline-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <Phone size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Sabit Hat</div>
                <div className="font-display text-2xl font-bold group-hover:text-orange-500 transition-colors">
                  {PHONE_NUMBER}
                </div>
                <div className="text-sm text-zinc-400 mt-1">Hafta içi 09:00 - 19:00 arası</div>
              </div>
            </a>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <MessageCircle size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">WhatsApp</div>
                <div className="font-display text-2xl font-bold group-hover:text-orange-500 transition-colors">
                  Mesaj Gönder
                </div>
                <div className="text-sm text-zinc-400 mt-1">Anında dönüş alın</div>
              </div>
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <Mail size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">E-posta</div>
                <div className="font-display text-xl font-bold group-hover:text-orange-500 transition-colors break-all">
                  {EMAIL}
                </div>
              </div>
            </a>

            <div className="flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900">
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <MapPin size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Adres</div>
                <div className="text-base text-white leading-relaxed">{ADDRESS}</div>
              </div>
            </div>

            <a
              href={`https://instagram.com/${INSTAGRAM}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-card"
              className="group flex items-start gap-5 p-6 bg-zinc-950 border border-zinc-900 hover:border-orange-600 transition-colors"
            >
              <div className="w-12 h-12 bg-orange-600 flex items-center justify-center shrink-0">
                <Instagram size={22} className="text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Instagram</div>
                <div className="font-display text-xl font-bold group-hover:text-orange-500 transition-colors">
                  @{INSTAGRAM}
                </div>
              </div>
            </a>
          </div>

          {/* Form */}
          <div className="bg-zinc-950 border border-zinc-900 p-8 md:p-10">
            <h3 className="font-display text-3xl font-bold uppercase mb-2 tracking-tight">
              Bize Yazın
            </h3>
            <p className="text-zinc-400 text-sm mb-8">
              Form göndererek bilgi almak istediğiniz konuyu iletebilirsiniz.
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Adınız *</label>
                <input
                  data-testid="contact-name-input"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white transition-colors"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Telefon *</label>
                  <input
                    data-testid="contact-phone-input"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">E-posta</label>
                  <input
                    data-testid="contact-email-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Konu</label>
                <input
                  data-testid="contact-subject-input"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Mesajınız *</label>
                <textarea
                  data-testid="contact-message-input"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  className="w-full bg-transparent border border-zinc-800 focus:border-orange-500 outline-none px-4 py-3 text-white resize-none transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
              >
                {sending ? "Gönderiliyor..." : (<><Send size={16} /> Mesajı Gönder</>)}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

